import { useTranslation } from "react-i18next";
import { formatEur, formatPercent } from "../../lib/format-kpi";
import type { InvestmentListItem } from "../../lib/investments";

export interface ErtraegeAggregate {
  /** Dividenden YTD ueber alle nicht-skippten Positionen, in CENTS. */
  dividendsYtdEur: number;
  /** Dividenden Lifetime ueber alle nicht-P2P, in CENTS. */
  dividendsLifetimeWertpapiereEur: number;
  /** Dividenden Lifetime ueber alle P2P, in CENTS. */
  dividendsLifetimeP2pEur: number;
  /** Yield-on-Cost YTD (dividendsYtd / purchaseValue * 100). */
  yocYtdPct: number | null;
  /** Yield-on-Cost Lifetime fuer Wertpapiere (non-P2P). */
  yocLifetimeWertpapierePct: number | null;
  /** Yield-on-Cost Lifetime fuer P2P. */
  yocLifetimeP2pPct: number | null;
  /** Realisierte G/V YTD ueber alle nicht-skippten Positionen, in CENTS. */
  realizedYtdEur: number;
  /** Realisierte G/V Lifetime ueber alle nicht-skippten Positionen, in CENTS. */
  realizedLifetimeEur: number;
  /** Kaufwert ueber alle nicht-skippten Positionen (alle), in CENTS. */
  purchaseValueEur: number;
  /** Symbole, deren purchaseValueEur null waren. */
  skipped: string[];
}

/**
 * Aggregiert Ertraege-KPIs (Dividenden + Realisierte G/V) ueber die uebergebenen
 * Positionen. Skipped jede Position, deren purchaseValueEur null ist.
 *
 * Dividenden + YoC werden in Wertpapiere (non-P2P) und P2P getrennt, damit User
 * Aktien/ETF-Dividenden von P2P-Zinsen unterscheiden kann. YTD-Dividenden bleiben
 * als Hauptwert gemeinsam (= Erloese-Stroeme dieses Jahres).
 *
 * Achtung: dividendsNetEur/realizedGainEur/realizedYtdEur/purchaseValueEur sind
 * trotz "Eur"-Suffix in CENTS — siehe Code-Konvention. Bei YoC-Prozenten kuerzt
 * sich der cents/euro-Faktor aus, weil Zaehler und Nenner beide cents sind.
 */
export function computeErtraegeAggregate(items: InvestmentListItem[]): ErtraegeAggregate {
  const skipped: string[] = [];
  let dividendsYtdEur = 0;
  let dividendsLifetimeWertpapiereEur = 0;
  let dividendsLifetimeP2pEur = 0;
  let realizedYtdEur = 0;
  let realizedLifetimeEur = 0;
  let purchaseValueEur = 0;
  let purchaseValueWertpapiereEur = 0;
  let purchaseValueP2pEur = 0;
  for (const it of items) {
    if (it.calc.purchaseValueEur === null) {
      skipped.push(it.symbol);
      continue;
    }
    purchaseValueEur += it.calc.purchaseValueEur;
    const divLifetime = it.calc.dividendsNetEur ?? 0;
    if (it.kind === "P2P" || it.kind === "BETEILIGUNG") {
      // BETEILIGUNG (Windrad/Buergerwind) wird zusammen mit P2P im
      // saldobasierten Ertraege-Bucket erfasst — gleiche Mechanik (Interest
      // = Zins-Cashflows = Ertrag).
      dividendsLifetimeP2pEur += divLifetime;
      purchaseValueP2pEur += it.calc.purchaseValueEur;
    } else {
      dividendsLifetimeWertpapiereEur += divLifetime;
      purchaseValueWertpapiereEur += it.calc.purchaseValueEur;
    }
    realizedLifetimeEur += it.calc.realizedGainEur ?? 0;
    if (it.periodReturn) {
      dividendsYtdEur += it.periodReturn.dividendsNetEur ?? 0;
      realizedYtdEur += it.periodReturn.realizedYtdEur;
    }
  }
  return {
    dividendsYtdEur,
    dividendsLifetimeWertpapiereEur,
    dividendsLifetimeP2pEur,
    yocYtdPct: purchaseValueEur > 0 ? (dividendsYtdEur / purchaseValueEur) * 100 : null,
    yocLifetimeWertpapierePct:
      purchaseValueWertpapiereEur > 0
        ? (dividendsLifetimeWertpapiereEur / purchaseValueWertpapiereEur) * 100
        : null,
    yocLifetimeP2pPct:
      purchaseValueP2pEur > 0 ? (dividendsLifetimeP2pEur / purchaseValueP2pEur) * 100 : null,
    realizedYtdEur,
    realizedLifetimeEur,
    purchaseValueEur,
    skipped,
  };
}

/** Signed-EUR-Anzeige fuer cent-Eingaben: prependet "+" wenn positiv. */
function formatSignedEur(cents: number): string {
  const prefix = cents > 0 ? "+" : "";
  return `${prefix}${formatEur(cents)}`;
}

export function ErtraegeKpiCard({ items }: { items: InvestmentListItem[] }) {
  const { t } = useTranslation();
  const a = computeErtraegeAggregate(items);
  const validCount = items.length - a.skipped.length;
  return (
    <div className="border rounded-lg p-4 shadow-sm bg-white">
      <div className="text-sm text-slate-600 font-medium">
        {t("investments.kpi.ertraege.title")}
      </div>
      <div className="text-xs text-slate-500 mt-1">{t("investments.kpi.ertraege.subtitle")}</div>

      {/* Hauptwert: Dividenden YTD gross (analog Depotwert) */}
      <div className="text-3xl font-mono font-medium mt-2 text-emerald-700">
        {formatEur(a.dividendsYtdEur)}
      </div>
      {/* Sub-Hauptwert: Realisierte G/V YTD klein darunter */}
      <div
        className={`text-xs font-mono mt-0.5 ${
          a.realizedYtdEur < 0 ? "text-red-700" : "text-slate-500"
        }`}
      >
        {t("investments.kpi.ertraege.realized_short")} {formatSignedEur(a.realizedYtdEur)}
      </div>

      {/* Lifetime-Block: Wertpapiere + P2P getrennt, dann Realisiert */}
      <div className="mt-3 pt-2 border-t border-slate-100 space-y-1 text-xs">
        <LifetimeLine
          label={t("investments.kpi.ertraege.dividends_wertpapiere_label", "Div. Wertpapiere ges.")}
          eur={a.dividendsLifetimeWertpapiereEur}
          pct={a.yocLifetimeWertpapierePct}
          pctSuffix="YoC"
          forceColor="emerald"
        />
        <LifetimeLine
          label={t("investments.kpi.ertraege.dividends_p2p_label", "Div. P2P ges.")}
          eur={a.dividendsLifetimeP2pEur}
          pct={a.yocLifetimeP2pPct}
          pctSuffix="YoC"
          forceColor="emerald"
        />
        <LifetimeLine
          label={t("investments.kpi.ertraege.realized_lifetime_label")}
          eur={a.realizedLifetimeEur}
          pct={null}
        />
      </div>

      {a.skipped.length > 0 && (
        <div className="mt-2 text-[10px] text-amber-700" title={a.skipped.join(", ")}>
          {t("investments.kpi.skipped_badge", {
            n: validCount,
            m: items.length,
          })}
        </div>
      )}
    </div>
  );
}

function LifetimeLine({
  label,
  eur,
  pct,
  pctSuffix,
  forceColor,
}: {
  label: string;
  /** Wert in cents. */
  eur: number;
  /** Prozent (optional, z. B. YoC). */
  pct: number | null;
  /** Suffix hinter dem %-Wert (z. B. "YoC"). */
  pctSuffix?: string;
  /** Erzwingt eine Farbe unabhaengig vom Vorzeichen. */
  forceColor?: "emerald";
}) {
  const neg = eur < 0;
  const color =
    forceColor === "emerald" ? "text-emerald-700" : neg ? "text-red-700" : "text-emerald-700";
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono ${color}`}>
        {pct !== null
          ? `${formatPercent(pct, 2)}${pctSuffix ? ` ${pctSuffix}` : ""} │ `
          : `${neg ? "↓" : "↑"} `}
        {formatSignedEur(eur)}
      </span>
    </div>
  );
}
